import React, {PropTypes} from 'react';
import {connect} from 'react-redux';
import _ from 'lodash';
import {Button, Icon} from '@goguardian/based';
import cn from 'classnames';

import Loading from '_shared/components/loading';
import AssociateLead from '_shared/components/associateLead';
import {track} from '../../../track';

import style from './style.scss';

const productData = [
  {
    id: 1,
    shortName: 'admin',
    name: 'GoGuardian Admin™',
    desc: 'GoGuardian Admin helps IT Administrators and CIOs keep their students safer online, monitor device usage activity, prevent cyberbullying, and can even help schools achieve CIPA compliance.',
    features: [
      'Advanced Web Filtering',
      'Activity Monitoring',
      'Detailed Usage Analytics',
      'Flagged Activity Reporting',
      'Missing Device Recovery'
    ]
  },
  {
    id: 2,
    shortName: 'teacher',
    name: 'GoGuardian Teacher™',
    desc: 'GoGuardian Teacher simplifies classroom technology management and helps teachers better connect with their students. Google Classroom integration makes setup a breeze.',
    features: [
      'View student activity (live)',
      'View student screens (live)',
      'Share links and files',
      'Lock devices for focus time',
      'Open and close tabs remotely'
    ]
  },
  {
    id: 3,
    shortName: 'fleet',
    name: 'GoGuardian Fleet™',
    desc: 'GoGuardian Fleet is the easiest way for IT Admins to manage their entire Chromebook deployments, all from one simple dashboard.',
    features: [
      'Bulk edit status, OUs, and users',
      'Track and export all your repairs',
      'Edit one device/user or many',
      'View your Chromebook inventory at a glance',
      'Google Admin console sync'
    ]
  },
  {
    id: 4,
    shortName: 'director',
    name: 'GoGuardian Director™',
    desc: 'GoGuardian Director is a free product that gives curriculum leaders a more transparent view into use and adoption of all educational apps and websites across their district.',
    features: [
      'Review software usage and adoption',
      'Track ROI of software purchases',
      'Identify engagement trends',
      'Plan for new procurement'
    ]
  }
];

class Products extends React.Component {
  constructor(props) {
    super(props);

    let selected = [];
    if (this.props.requestedProduct) {
      selected.push(this.props.requestedProduct);
    }
    this.state = {
      doSave: false,
      selected: selected
    };
  }

  componentDidMount() {
    track('signupFlow', {
      step: '/products',
      productId: this.props.requestedProduct
    });
  }

  toggleSelect(product, e) {
    e.stopPropagation();
    let selected = this.state.selected;
    if (_.indexOf(selected, product) === -1) {
      selected.push(product);
    } else {
      selected = selected.filter(p => p !== product)
    }
    this.setState({
      selected: selected
    });
  }

  continue() {
    this.setState({
      doSave: true
    });
  }

  renderProducts() {
    const tempProducts = productData.filter(p => p.id !== this.props.requestedProduct);
    tempProducts.unshift(_.find(productData, {id: this.props.requestedProduct}));
    const products = tempProducts.map((product, i) => {
      const productIsEnabled = _.indexOf(this.props.enabledProducts, product.id) !== -1;
      const productIsSelected = _.indexOf(this.state.selected, product.id) !== -1;
      const imageStyle = cn([style[product.shortName]], {[style.selected]: productIsSelected});
      const productStyle = cn([style.product], {[style.purchased]: productIsEnabled});
      const buttonClick = productIsEnabled ? null : this.toggleSelect.bind(this, product.id);

      const featureList = product.features.map((feature, j) => {
        return (
          <li key={j}>{feature}</li>
        );
      });

      let button, description, image;

      if (product.shortName === 'director') {
        description = (
          <div className={style.productDesc}>
            GoGuardian Director is a <b>free product</b> that gives curriculum leaders a more transparent view into use and adoption of all educational apps and websites across their district.
          </div>
        );

        if (productIsSelected) {
          button = (
            <div className={style.removeButton}>
              <Button
              onClick={buttonClick}
              type="secondary"
              block
              >
                Remove free product
              </Button>
            </div>
          );
        } else {
          button = (
            <div className={style.freeProduct}>
              <Button
              onClick={buttonClick}
              block
              >
                Add free product
              </Button>
            </div>
          );
        }
      } else {
        description = (
          <div className={style.productDesc}>
            {product.desc}
          </div>
        );

        if (productIsEnabled) {
          button = (
            <div className={style.removeButton}>
              <Button
                onClick={buttonClick}
                type="secondary"
                disabled
                block
              >
                Product enabled
              </Button>
            </div>
          );
        } else if (productIsSelected) {
          button = (
            <div className={style.removeButton}>
              <Button
                onClick={buttonClick}
                type="secondary"
                block
              >
                Remove free trial
              </Button>
            </div>
          );
        } else {
          button = (
            <div>
              <Button
                onClick={buttonClick}
                block
              >
                Add free trial
              </Button>
            </div>
          );
        }
      }

      if (productIsEnabled || productIsSelected) {

        image = (
          <div className={style.imageContainer}>
            <div className={style.imageCheck}>
              <Icon value="check" />
            </div>
            <div className={imageStyle}>
            </div>
          </div>
        );
      } else {
        image = (
          <div className={imageStyle}>
          </div>
        );
      }

      return (
        <div className={productStyle} key={i} onClick={buttonClick}>
          {image}
          <h2>{product.name}</h2>
          {description}
          <ul>
            {featureList}
          </ul>
          {button}
        </div>
      );
    });

    const allowContinue = this.state.selected.length > 0;
    const type = allowContinue ? "primary" : "secondary";

    return (
      <div className={style.productsContainer}>
        <h1>Product Selection</h1>
        <div className={style.selectionDescription}>
          <span>Select the GoGuardian products you&#39;d like to add to your account, then click "Continue".</span>
          <Button
            type={type}
            onClick={this.continue.bind(this)}
            disabled={!allowContinue}>
              Continue
          </Button>
        </div>
        <div className={style.productList}>
          {products}
        </div>

        <AssociateLead />
      </div>
    );
  }

  renderLoader() {
    return (
      <Loading actionName="enableProducts" actionParams={[this.state.selected]}>
        We are enabling your products...
      </Loading>
    )
  }

  render() {
    return this.state.doSave ? this.renderLoader() : this.renderProducts();
  }
};

const mapStateToProps = (state) => {
  let enabledProducts = [];
  if (state.isProductEnabled && state.isProductEnabled.data) {
    enabledProducts = state.isProductEnabled.data.enabled;
  }
  return {
    enabledProducts: enabledProducts,
    requestedProduct: state.setProduct
  };
};

export default connect(mapStateToProps)(Products);



/** WEBPACK FOOTER **
 ** ./src/app/components/products/index.jsx
 **/